import { Sticky } from '@/config/sticky-layout';
import { env } from '@/env.mjs';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { isSingleOrder, useUserOffers } from '@/hooks/use-user-offers';
import { useAuthentication } from '@/hooks/useAuthentication';
import { downloadFile } from '@/utils/download';
import { getCSVDocumentString } from '@/utils/getCSV';
import { routes } from '@/utils/routes';
import {
  Box,
  BoxProps,
  Button,
  Container,
  Divider,
  Flex,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { compareAddresses, formatAddress, formatTokenAmount } from '@onbeam/utils';
import { ExportIcon } from '@sphere/icons';
import { TableEmptyRows, TableHeader, TableSkeletonRows } from '@sphere/ui';
import { compareDesc, formatISO, parseISO } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { FC, useCallback, useMemo } from 'react';
import RefreshButton from '../../Search/RefreshButton';
import { AccountOfferItemDesktop } from './AccountOfferItemDesktop';
import { AccountOfferItemMobile } from './AccountOfferItemMobile';

type Props = BoxProps & {
  address: string;
};

export const AccountOffers: FC<Props> = ({ address, ...boxProps }) => {
  const { t } = useTranslation('account');
  const { t: tActivity } = useTranslation('account');
  const marketplaceChain = useMarketplaceChain();
  const { userAddress } = useAuthentication();

  const isOwner = compareAddresses(userAddress, address);

  const {
    data,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isInitialLoading,
    isLoading,
    isRefetching,
  } = useUserOffers(address);

  const offers = useMemo(
    () => data.sort((a, b) => compareDesc(parseISO(a?.createdAt), parseISO(b?.createdAt))),
    [data],
  );

  const handleDownload = useCallback(() => {
    const documentData = offers.map(offer => {
      const collectionId = offer.criteria?.data?.collection?.id || offer.contract || '';
      return {
        [t('offers.table.headers.token-id')]: isSingleOrder(offer)
          ? offer.criteria?.data?.token?.tokenId
          : '-',
        [t('offers.table.headers.contract-name')]: formatAddress(
          offer.criteria?.data?.collection?.name,
        ),
        [t('offers.table.headers.type')]: isSingleOrder(offer)
          ? tActivity('event-titles.offer')
          : tActivity('event-titles.collection-offer'),
        [t('offers.table.headers.amount')]: formatTokenAmount(offer.price?.amount?.decimal),
        [t('offers.table.headers.currency')]: offer.price?.currency?.symbol || '',
        [t('offers.table.headers.created')]: formatISO(parseISO(offer.createdAt)),
        [t('offers.table.headers.expires')]: offer.validUntil
          ? formatISO(parseISO(new Date(offer.validUntil * 1000).toISOString()))
          : '',
        [t('offers.table.headers.link')]:
          env.NEXT_PUBLIC_HOST_URL +
          (isSingleOrder(offer)
            ? routes.nft.detail(
                collectionId,
                offer.criteria?.data?.token?.tokenId ?? '',
                marketplaceChain.routePrefix,
              )
            : routes.collections.detail(collectionId, marketplaceChain.routePrefix)),
      };
    });

    const documentString = getCSVDocumentString(documentData);
    const fileName = `${formatISO(new Date())}-offers.csv`;

    downloadFile(documentString, fileName);
  }, [offers, t, marketplaceChain.routePrefix, tActivity]);

  return (
    <Box py="space.24" {...boxProps}>
      <Container variant="breakout">
        <Flex minH={rem(40)} alignItems="center" justifyContent="space-between">
          <RefreshButton
            size="sm"
            onClick={() => refetch()}
            isLoading={isRefetching}
            tooltipLabel={time => t('common:last-updated', { time })}
          />
          <Button
            variant="link"
            onClick={handleDownload}
            rightIcon={<ExportIcon boxSize={rem(14)} />}
            data-testid="account-offers-export-button"
            fontWeight="medium"
            isDisabled={!offers.length}
          >
            {t('offers.actions.export-csv.label')}
          </Button>
        </Flex>

        <Divider my="space.8" display={{ base: 'none', md: 'block' }} />

        <Table
          variant="unstyled"
          position="relative"
          display={{ base: 'none', md: 'table' }}
          style={{ borderCollapse: 'separate', borderSpacing: `0 ${rem(8)}` }}
        >
          <Sticky
            as={Thead}
            element="filter-bar"
            zIndex="sticky-layout-contained"
            stuck={{
              ...(offers.length > 1 && {
                animate: {
                  boxShadow: 'sticky.table-header',
                },
              }),
            }}
          >
            <Tr _hover={{}} borderBottom="none">
              <TableHeader pl={0}>{t('offers.table.headers.item')}</TableHeader>
              <TableHeader>{t('offers.table.headers.type')}</TableHeader>
              <TableHeader textAlign="right">{t('offers.table.headers.amount')}</TableHeader>
              <TableHeader>{t('offers.table.headers.created')}</TableHeader>
              <TableHeader>{t('offers.table.headers.expires')}</TableHeader>
              {isOwner && <TableHeader />}
            </Tr>
          </Sticky>
          <Tbody>
            {isLoading ? (
              <TableSkeletonRows rows={20} cols={7} height={rem(80)} />
            ) : (
              offers.map(offer => (
                <AccountOfferItemDesktop key={offer.id} offer={offer} refetch={refetch} />
              ))
            )}
          </Tbody>
        </Table>

        <Box display={{ md: 'none' }} mt="space.8">
          {isLoading ? (
            <TableSkeletonRows rows={20} height={{ base: rem(185), sm: rem(135) }} isMobile />
          ) : (
            offers.map(offer => (
              <AccountOfferItemMobile key={offer.id} offer={offer} refetch={refetch} />
            ))
          )}
        </Box>

        {!isInitialLoading && !offers.length && (
          <>
            <Text textAlign="center" fontSize="md" color="gray.5" my="space.32">
              {t(isOwner ? 'offers.empty.own-account' : 'offers.empty.other-account')}
            </Text>
            <TableEmptyRows />
          </>
        )}

        {hasNextPage && (
          <Flex justifyContent="center">
            <Button
              mt="space.16"
              variant="secondary"
              onClick={() => fetchNextPage()}
              isLoading={isFetchingNextPage}
            >
              {t('table.load-more')}
            </Button>
          </Flex>
        )}
      </Container>
    </Box>
  );
};
